/**
 * KpiTile — single big number + label + sparkline + delta.
 *
 * Expected data shape:
 *   { label: string; value: string|number; unit?: string;
 *     delta?: string; deltaDirection?: "up"|"down";
 *     spark?: number[] }
 */
import * as React from "react";
import { cn } from "@/lib/cn";
import type { TileBodyProps, TileEditorProps } from "../registry";

interface KpiData {
  label?: string;
  value?: string | number;
  unit?: string;
  delta?: string;
  deltaDirection?: "up" | "down";
  spark?: number[];
}

export function KpiTile({ data }: TileBodyProps) {
  const d = data as KpiData;
  const value = d.value ?? "—";
  // No series, no sparkline — this drew a made-up rising line when the
  // agent sent none, which reads as a trend that does not exist.
  const spark = d.spark ?? [];
  const sparkPath = React.useMemo(() => buildSpark(spark), [spark]);

  return (
    <div className="flex h-full flex-col justify-between gap-2">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint">
          {d.label ?? "Metric"}
        </div>
        <div className="mt-1 flex items-baseline gap-1 text-fg">
          <span className="text-[clamp(28px,4vw,48px)] font-semibold leading-none tracking-tight">
            {value}
          </span>
          {d.unit && (
            <span className="text-[12px] font-medium text-fg-faint">
              {d.unit}
            </span>
          )}
        </div>
      </div>

      {spark.length > 1 && (
        <div className="h-[28px]">
          <svg
            viewBox="0 0 100 30"
            preserveAspectRatio="none"
            className="h-full w-full"
          >
            <path d={sparkPath.area} className="fill-accent/15" />
            <path
              d={sparkPath.line}
              fill="none"
              className="stroke-accent"
              strokeWidth="1.5"
            />
          </svg>
        </div>
      )}

      {d.delta && (
        <div className="flex items-baseline justify-between font-mono text-[11px] text-fg-muted">
          <span>vs prior</span>
          <span
            className={cn(
              d.deltaDirection === "down" ? "text-danger" : "text-success",
            )}
          >
            {d.deltaDirection === "down" ? "▼" : "▲"} {d.delta}
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Editor ─────────────────────────────────────────────────────────────

/** Inline editor for a KPI tile: label / value / unit / delta. Keeps the
 * `spark` series and `refresh`; sends only the kind's own fields. */
export function KpiEditor({ data, onSave, onCancel }: TileEditorProps) {
  const d = data as KpiData;
  const [label, setLabel] = React.useState(d.label ?? "");
  const [value, setValue] = React.useState(String(d.value ?? ""));
  const [unit, setUnit] = React.useState(d.unit ?? "");
  const [delta, setDelta] = React.useState(d.delta ?? "");
  const [dir, setDir] = React.useState<"up" | "down">(d.deltaDirection ?? "up");

  const save = () =>
    onSave({
      data: {
        ...(d.spark ? { spark: d.spark } : {}),
        ...(typeof data.refresh === "string" ? { refresh: data.refresh } : {}),
        label: label.trim(),
        value: value.trim(),
        unit: unit.trim(),
        delta: delta.trim(),
        deltaDirection: dir,
      },
    });

  const field =
    "border border-border bg-bg px-2 py-1 text-[13px] text-fg outline-none focus:border-accent";

  return (
    <form
      className="flex h-full flex-col gap-2 overflow-auto text-[13px]"
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
    >
      <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label" className={field} />
      <div className="flex gap-2">
        <input value={value} onChange={(e) => setValue(e.target.value)} placeholder="Value" className={cn(field, "flex-1")} />
        <input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="Unit" className={cn(field, "w-20")} />
      </div>
      <div className="flex gap-2">
        <input value={delta} onChange={(e) => setDelta(e.target.value)} placeholder="Delta (e.g. 12%)" className={cn(field, "flex-1")} />
        <select value={dir} onChange={(e) => setDir(e.target.value as "up" | "down")} className={cn(field, "w-20")}>
          <option value="up">▲ up</option>
          <option value="down">▼ down</option>
        </select>
      </div>
      <div className="mt-auto flex items-center justify-end gap-2">
        <button type="button" onClick={onCancel} className="h-7 px-3 text-[12px] text-fg-muted transition hover:text-fg">
          Cancel
        </button>
        <button type="submit" className="h-7 bg-fg px-3 text-[12px] font-medium text-bg transition hover:bg-accent hover:text-accent-fg">
          Save
        </button>
      </div>
    </form>
  );
}

function buildSpark(values: number[]): { line: string; area: string } {
  if (!values.length) return { line: "", area: "" };
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const sx = (i: number) => (i / Math.max(1, values.length - 1)) * 100;
  const sy = (v: number) => 30 - ((v - min) / range) * 28;
  const line = values
    .map((v, i) => `${i === 0 ? "M" : "L"}${sx(i).toFixed(2)},${sy(v).toFixed(2)}`)
    .join(" ");
  const area = `${line} L100,30 L0,30 Z`;
  return { line, area };
}
